import React from 'react'
import { Box, TextField, Typography } from '@mui/material'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import { toast } from 'react-toastify'
import { ApiPushCreateCampaign } from '../../apiService/Core/Apicall'
import DarkDialog from './DarkDialog'
import { InlineAlert } from './PushDisabledNotice'
import { formFieldSx } from './pushStyles'
import { fmtNum, getErrorMessage, getErrorStatus, isPushDisabled, newIdempotencyKey } from './pushUtils'
import type { AudiencePayload, Campaign } from './types'

interface Props {
    open: boolean
    onClose: () => void
    title: string
    body: string
    destination?: string
    audience: AudiencePayload
    sendable: number
    onSent: (campaign: Campaign) => void
    /** Called on 409 so the page can refresh the preview. */
    onConflict: (message: string) => void
    onFieldErrors?: (errors: Record<string, string>) => void
}

const SendConfirmDialog: React.FC<Props> = ({
    open, onClose, title, body, destination, audience, sendable, onSent, onConflict, onFieldErrors,
}) => {
    const [typed, setTyped] = React.useState('')
    const [error, setError] = React.useState<string | null>(null)
    const [submitting, setSubmitting] = React.useState(false)
    const keyRef = React.useRef('')
    const submittingRef = React.useRef(false)

    // One idempotency key per confirm session: a double click / retry re-uses it.
    React.useEffect(() => {
        if (open) {
            keyRef.current = newIdempotencyKey()
            submittingRef.current = false
            setTyped('')
            setError(null)
            setSubmitting(false)
        }
    }, [open])

    const canSend = typed.trim() === String(sendable) && sendable > 0 && !submitting

    const handleSend = async () => {
        if (!canSend || submittingRef.current) return
        submittingRef.current = true
        setSubmitting(true)
        setError(null)
        try {
            const res = await ApiPushCreateCampaign({
                title,
                body,
                ...(destination ? { destination } : {}),
                audience,
                idempotencyKey: keyRef.current,
                confirmedSendable: sendable,
            })
            toast.success('Campaign accepted and queued')
            onSent(res?.data?.campaign)
        } catch (e: any) {
            const status = getErrorStatus(e)
            const message = getErrorMessage(e)
            if (status === 409) {
                toast.warn(message)
                onConflict(message)
                onClose()
                return
            }
            if (status === 422 && Array.isArray(e?.errors)) {
                const map: Record<string, string> = {}
                e.errors.forEach((er: any) => {
                    if (er?.field) map[String(er.field).replace(/^body\./, '')] = er.message
                })
                onFieldErrors?.(map)
            }
            setError(isPushDisabled(e) ? 'Push campaigns are not enabled on the server.' : message)
        } finally {
            submittingRef.current = false
            setSubmitting(false)
        }
    }

    return (
        <DarkDialog
            open={open}
            onClose={onClose}
            title="Confirm send"
            icon={<SendOutlinedIcon sx={{ fontSize: 18 }} />}
            busy={submitting}
            confirmText="Send"
            onConfirm={handleSend}
            confirmDisabled={!canSend}
        >
            <Typography sx={{ color: '#d0d0d0', fontSize: 14, mb: 2 }}>
                This will send &quot;{title}&quot; to a frozen audience of{' '}
                <strong style={{ color: '#ff6a3d' }}>{fmtNum(sendable)}</strong> users. It cannot be recalled once sent.
            </Typography>
            <InlineAlert severity="info" sx={{ mb: 2 }}>
                Acceptance by Firebase is not confirmed delivery to the device. The campaign report shows what Firebase
                accepted or rejected for each user.
            </InlineAlert>
            <Typography sx={{ color: '#9a9a9a', fontSize: 12, mb: 1 }}>
                Type <strong style={{ color: '#ff6a3d' }}>{sendable}</strong> to confirm.
            </Typography>
            <TextField
                fullWidth
                size="small"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={String(sendable)}
                disabled={submitting}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                sx={formFieldSx}
            />
            {error && (
                <Box sx={{ mt: 2 }}>
                    <InlineAlert severity="error">{error}</InlineAlert>
                </Box>
            )}
        </DarkDialog>
    )
}

export default SendConfirmDialog
